import { useState, useCallback } from 'react';
import { useAdminFetch, postInternal } from '../api/fetch';
import { useNotify } from '../components/NotificationProvider';
import { BulkUserInput } from '../utils/csvUtils';

export interface User {
  id: string;
  userName: string;
  email: string;
  lectures: UserLecture[];
}

export interface UserLecture {
  id: string;
  name: string;
}

export interface Lecture {
  id: string;
  lectureName: string;
}

export interface ActionLog {
  id: string;
  action: string;
  detail: string;
  createdAt: string;
}

// POST /api/admin/users/bulk のレスポンス
export interface BulkRegisterResult {
  createdCount: number;
  failedCount: number;
  created: { row: number; email: string }[];
  failed: { row: number; email: string; reason: string }[];
  message: string;
}

export interface UseUserDataProps {
  onError?: (message: string) => void;
}

export const useUserData = ({ onError }: UseUserDataProps = {}) => {
  const notify = useNotify();
  const handleError = onError ?? notify;
  const [users, setUsers] = useState<User[]>([]);
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [actionLogs, setActionLogs] = useState<ActionLog[]>([]);
  const { get, post } = useAdminFetch();

  const fetchUsers = useCallback(async () => {
    try {
      const result = await get<{
        users: User[];
        lectures: Lecture[];
      }>('/api/admin/users');
      
      setUsers(result.users);
      setLectures(result.lectures);
      return true;
    } catch (error) {
      handleError("Can't get user data from db properly.");
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActionLogs = useCallback(async (userId: string) => {
    try {
      const response = await get<{
        data: ActionLog[];
        message: string;
      }>(`/api/admin/user/action/${userId}`);
      
      console.log(response.message);
      setActionLogs(response.data);
      return true;
    } catch (error) {
      handleError("Can't get action log data from db properly.");
      setActionLogs([]);
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createUser = useCallback(async (
    userName: string,
    email: string,
    password: string,
    selectedLectureIds: string[]
  ) => {
    try {
      const result = await post<{
        success: string;
      }>('/api/admin/user/create', {
        body: JSON.stringify({
          user_from_react: userName,
          email_from_react: email,
          pass_from_react: password,
          select_lecture_from_react: selectedLectureIds,
        }),
      });

      return result.success;
    } catch (error) {
      handleError("Failed to create user.");
      return "error";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CSV 由来の受講者を一括登録する。
  // NOTE: 共通の useAdminFetch.post は 500/404 で navigate("/") してしまい(fetch.ts checkStatus)、
  //       ダイアログと入力内容が失われる。一括登録は画面遷移させずダイアログ内に結果表示したいので、
  //       checkStatus を通さない postInternal を直接使い、失敗時は null を返してダイアログ側で通知する。
  const bulkCreateUsers = useCallback(async (
    users: BulkUserInput[],
    lectureIds: string[]
  ): Promise<BulkRegisterResult | null> => {
    try {
      const token = localStorage.getItem("admin_token") ?? "";
      const response = await postInternal(
        "/api/admin/users/bulk",
        { body: JSON.stringify({ users, lecture_ids: lectureIds }) },
        token
      );
      if (!response.ok) {
        return null;
      }
      return (await response.json()) as BulkRegisterResult;
    } catch (error) {
      return null;
    }
  }, []);

  // 一括登録プレビュー用: email 配列のうち既にDBに存在するものを返す(単一バッチ問い合わせ)。
  // 失敗時は null(プレビューの事前表示はスキップし、最終判定はサーバの一括登録に委ねる)。
  const checkExistingEmails = useCallback(async (
    emails: string[]
  ): Promise<string[] | null> => {
    if (emails.length === 0) return [];
    try {
      const token = localStorage.getItem("admin_token") ?? "";
      const response = await postInternal(
        "/api/admin/users/exists",
        { body: JSON.stringify({ emails }) },
        token
      );
      if (!response.ok) return null;
      const data = (await response.json()) as { existing: string[] };
      return data.existing ?? [];
    } catch (error) {
      return null;
    }
  }, []);

  return {
    users,
    lectures,
    actionLogs,
    fetchUsers,
    fetchActionLogs,
    createUser,
    bulkCreateUsers,
    checkExistingEmails
  };
};